import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, Chip, Tooltip, Divider, CircularProgress,
  Avatar, AvatarGroup, Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { authFetch } from '../../utils/authHelpers';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  'In progress': '#3b82f6',
  'Offerte': '#f59e0b',
  'On Hold': '#adb3b5',
  'Afgerond': '#10b981',
  'Pre-productie': '#8b5cf6',
  'Post-productie': '#ec4899',
};

const INSIGHT_ICONS = {
  status: <InfoOutlinedIcon sx={{ fontSize: 16 }} />,
  deadline: <EventIcon sx={{ fontSize: 16 }} />,
  action_item: <AssignmentIcon sx={{ fontSize: 16 }} />,
  client_question: <HelpOutlineIcon sx={{ fontSize: 16 }} />,
};

const INSIGHT_LABELS = {
  status: 'Status',
  deadline: 'Deadlines',
  action_item: 'Actiepunten',
  client_question: 'Klantvragen',
};

function Section({ title, icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', py: 0.5, px: 1, borderRadius: 1, '&:hover': { bgcolor: '#f8f9fa' } }}
      >
        {icon}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#767c7e', ml: 0.75, flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: '#adb3b5' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#adb3b5' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 1, pr: 0.5, pb: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

export default function ProjectOverviewPanel({ folderId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);

  const fetchOverview = useCallback(async () => {
    if (!folderId) return;
    setLoading(true);
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folderId}/project-overview`);
      if (res.ok) {
        setData(await res.json());
      }
    } catch (err) {
      console.error('[ProjectOverview] Failed:', err);
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  const fetchSuggestions = useCallback(async () => {
    if (!folderId) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folderId}/insights?status=suggested`);
      if (res.ok) setSuggestions(await res.json());
    } catch (err) { /* silent */ }
  }, [folderId]);

  useEffect(() => {
    fetchOverview();
    fetchSuggestions();
    // Refresh suggestions every 30 seconds
    const interval = setInterval(fetchSuggestions, 30000);
    return () => clearInterval(interval);
  }, [fetchOverview, fetchSuggestions]);

  const handleInsightAction = async (insightId, action) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folderId}/insights/${insightId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: action }),
      });
      if (res.ok) {
        toast.success(action === 'accepted' ? 'Insight geaccepteerd' : action === 'rejected' ? 'Insight afgewezen' : 'Insight vergrendeld');
        fetchOverview();
        fetchSuggestions();
      }
    } catch (err) {
      toast.error('Actie mislukt');
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: 320, borderLeft: '1px solid rgba(45,51,53,0.08)', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} sx={{ color: '#526075' }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ width: 320, borderLeft: '1px solid rgba(45,51,53,0.08)', bgcolor: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
        <Typography sx={{ fontSize: 13, color: '#adb3b5', textAlign: 'center' }}>
          Kon projectoverzicht niet laden
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  const { project, deadlines, insights } = data;
  const teamMembers = [];
  if (project.projectleider_names) teamMembers.push({ role: 'PL', name: project.projectleider_names });
  if (project.creative_names) teamMembers.push({ role: 'Creative', name: project.creative_names });
  if (project.editor_names) teamMembers.push({ role: 'Editor', name: project.editor_names });
  if (project.animator_names) teamMembers.push({ role: 'Animator', name: project.animator_names });
  if (project.cameraman_names) teamMembers.push({ role: 'Camera', name: project.cameraman_names });
  if (project.productie_names) teamMembers.push({ role: 'Productie', name: project.productie_names });

  return (
    <Box sx={{
      width: 320,
      borderLeft: '1px solid rgba(45,51,53,0.08)',
      bgcolor: '#fff',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 2, borderBottom: '1px solid #f1f4f5' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, color: '#767c7e', fontWeight: 600, letterSpacing: 0.5 }}>
            {project.project_number}
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#2d3335', lineHeight: 1.3, mt: 0.25 }}>
            {project.project_name_full}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
            {project.status && (
              <Chip
                label={project.status}
                size="small"
                sx={{
                  fontSize: 11, height: 22,
                  bgcolor: `${STATUS_COLORS[project.status] || '#767c7e'}15`,
                  color: STATUS_COLORS[project.status] || '#767c7e',
                  fontWeight: 600,
                }}
              />
            )}
            {project.project_type && (
              <Chip label={project.project_type} size="small" sx={{ fontSize: 11, height: 22, bgcolor: '#f1f4f5', color: '#767c7e' }} />
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {/* Klant */}
        {project.klant_name && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#adb3b5', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Klant</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#556071', mt: 0.25 }}>{project.klant_name}</Typography>
            {project.contactpersoon_name && (
              <Typography sx={{ fontSize: 12, color: '#767c7e' }}>{project.contactpersoon_name}</Typography>
            )}
          </Box>
        )}

        {/* Opdrachtwaarde */}
        {project.opdrachtwaarde != null && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#adb3b5', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Opdrachtwaarde</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#2d3335', mt: 0.25 }}>
              {'\u20AC'}{Number(project.opdrachtwaarde).toLocaleString('nl-NL', { minimumFractionDigits: 0 })}
            </Typography>
          </Box>
        )}

        {/* Team */}
        {teamMembers.length > 0 && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#adb3b5', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Team</Typography>
            {teamMembers.map((m) => (
              <Box key={m.role} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                <Typography sx={{ fontSize: 11, color: '#adb3b5', width: 60 }}>{m.role}</Typography>
                <Typography sx={{ fontSize: 12, color: '#556071' }}>{m.name}</Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Deadlines */}
        <Section title="Deadlines" icon={<EventIcon sx={{ fontSize: 16, color: '#f59e0b' }} />}>
          {deadlines.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: '#adb3b5', pl: 0.5 }}>Geen deadlines</Typography>
          ) : (
            deadlines.slice(0, 10).map((d, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}>
                <Chip
                  label={d.type === 'task' ? 'T' : 'D'}
                  size="small"
                  sx={{ fontSize: 10, height: 18, width: 18, minWidth: 18, bgcolor: d.type === 'task' ? '#dbeafe' : '#fce7f3', color: d.type === 'task' ? '#3b82f6' : '#ec4899' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, color: '#556071', lineHeight: 1.3 }} noWrap>{d.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#adb3b5' }}>{d.deadline}</Typography>
                </Box>
              </Box>
            ))
          )}
        </Section>

        {/* AI Suggestions */}
        {suggestions.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Section title="AI Suggesties" icon={<LightbulbOutlinedIcon sx={{ fontSize: 16, color: '#f59e0b' }} />}>
              {suggestions.map((s) => (
                <Box key={s.id} sx={{ p: 1, mb: 0.5, borderRadius: 1.5, border: '1px solid #fde68a', bgcolor: '#fffbeb' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    {INSIGHT_ICONS[s.insight_type]}
                    <Typography sx={{ fontSize: 10, color: '#92400e', fontWeight: 600, textTransform: 'uppercase' }}>
                      {INSIGHT_LABELS[s.insight_type] || s.insight_type}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#b45309', ml: 'auto' }}>
                      {Math.round((s.confidence || 0) * 100)}%
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: '#78350f', lineHeight: 1.4, mb: 0.5 }}>{s.content}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title="Accepteren">
                      <IconButton size="small" onClick={() => handleInsightAction(s.id, 'accepted')} sx={{ p: 0.25, color: '#10b981' }}>
                        <CheckCircleIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Afwijzen">
                      <IconButton size="small" onClick={() => handleInsightAction(s.id, 'rejected')} sx={{ p: 0.25, color: '#ef4444' }}>
                        <CancelIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Section>
          </>
        )}

        {/* Accepted Insights */}
        {Object.entries(insights).map(([type, items]) => {
          if (items.length === 0) return null;
          return (
            <React.Fragment key={type}>
              <Divider sx={{ my: 1 }} />
              <Section title={INSIGHT_LABELS[type] || type} icon={INSIGHT_ICONS[type] || <InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
                {items.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: '#556071', flex: 1, lineHeight: 1.4 }}>{item.content}</Typography>
                    {item.status === 'accepted' && (
                      <Tooltip title="Vergrendelen">
                        <IconButton size="small" onClick={() => handleInsightAction(item.id, 'locked')} sx={{ p: 0.25, color: '#d1d5db' }}>
                          <LockIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {item.status === 'locked' && <LockIcon sx={{ fontSize: 14, color: '#526075', mt: 0.25 }} />}
                  </Box>
                ))}
              </Section>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
